import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Mail, Lock, User, Phone, MailCheck, RefreshCw } from 'lucide-react';
import { registreerOpleider, verstuurVerificatieOpnieuw, foutMelding, OpleiderAccountData } from '../../services/opleiderOnboardingApi';
import { useAuthStore } from '../../store/authStore';
import { Kaart, StapKop, Veld, Knop, Melding } from './ui';

/** Stap 1a: account aanmaken (nog niet ingelogd). */
export const StapAccountFormulier: React.FC<{ onAangemaakt: () => void }> = ({ onAangemaakt }) => {
  const { t } = useTranslation('pages');
  const { login } = useAuthStore();
  const [form, setForm] = useState<OpleiderAccountData>({ email: '', password: '', password_confirm: '', contactpersoon: '', telefoon: '' });
  const zet = (veld: keyof OpleiderAccountData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [veld]: e.target.value }));

  const wachtwoordZwak =
    form.password.length > 0 && !(form.password.length >= 10 && /[a-z]/.test(form.password) && /[A-Z]/.test(form.password) && /\d/.test(form.password));
  const nietGelijk = form.password_confirm.length > 0 && form.password !== form.password_confirm;

  const registreer = useMutation({
    mutationFn: () => registreerOpleider(form),
    onSuccess: ({ user, token }) => {
      login(user, token);
      onAangemaakt();
    },
  });

  return (
    <Kaart>
      <StapKop
        nummer={1}
        titel={t('opleiderWizard.account.titel', { defaultValue: 'Maak uw opleidersaccount aan' })}
        intro={t('opleiderWizard.account.intro', {
          defaultValue: 'Start met uw primaire accountgegevens. We sturen direct een verificatiemail naar uw zakelijke adres.',
        })}
      />
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!wachtwoordZwak && !nietGelijk) registreer.mutate();
        }}
      >
        <Veld
          name="email"
          type="email"
          autoComplete="email"
          required
          label={t('opleiderWizard.account.email', { defaultValue: 'Zakelijk e-mailadres' })}
          icoon={<Mail className="h-4 w-4" />}
          value={form.email}
          onChange={zet('email')}
          placeholder="info@uwinstituut.nl"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Veld
            name="password"
            type="password"
            autoComplete="new-password"
            required
            label={t('opleiderWizard.account.wachtwoord', { defaultValue: 'Wachtwoord' })}
            icoon={<Lock className="h-4 w-4" />}
            value={form.password}
            onChange={zet('password')}
            hint={t('opleiderWizard.account.wachtwoordEis', { defaultValue: 'Minimaal 10 tekens, met hoofdletter, kleine letter en cijfer.' })}
            fout={wachtwoordZwak ? t('opleiderWizard.account.wachtwoordZwak', { defaultValue: 'Voldoet nog niet aan de eisen.' }) : null}
          />
          <Veld
            name="password_confirm"
            type="password"
            autoComplete="new-password"
            required
            label={t('opleiderWizard.account.herhaal', { defaultValue: 'Herhaal wachtwoord' })}
            icoon={<Lock className="h-4 w-4" />}
            value={form.password_confirm}
            onChange={zet('password_confirm')}
            fout={nietGelijk ? t('opleiderWizard.account.nietGelijk', { defaultValue: 'Wachtwoorden komen niet overeen.' }) : null}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Veld
            name="contactpersoon"
            autoComplete="name"
            required
            label={t('opleiderWizard.account.contactpersoon', { defaultValue: 'Naam hoofdcontactpersoon' })}
            icoon={<User className="h-4 w-4" />}
            value={form.contactpersoon}
            onChange={zet('contactpersoon')}
            hint={t('opleiderWizard.account.contactHint', { defaultValue: 'Deze persoon ondertekent later de overeenkomst.' })}
          />
          <Veld
            name="telefoon"
            type="tel"
            autoComplete="tel"
            required
            label={t('opleiderWizard.account.telefoon', { defaultValue: 'Direct telefoonnummer' })}
            icoon={<Phone className="h-4 w-4" />}
            value={form.telefoon}
            onChange={zet('telefoon')}
            placeholder="06 12345678"
          />
        </div>

        {registreer.isError && (
          <Melding>{foutMelding(registreer.error, t('opleiderWizard.account.fout', { defaultValue: 'Account aanmaken mislukt.' }))}</Melding>
        )}

        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-400">
            {t('opleiderWizard.account.alAccount', { defaultValue: 'Al een account?' })}{' '}
            <Link to="/login" className="font-medium text-sky-400 hover:text-sky-300">
              {t('opleiderWizard.account.inloggen', { defaultValue: 'Inloggen' })}
            </Link>
          </p>
          <Knop type="submit" bezig={registreer.isPending} disabled={wachtwoordZwak || nietGelijk}>
            {t('opleiderWizard.account.submit', { defaultValue: 'Account aanmaken' })}
          </Knop>
        </div>
      </form>
    </Kaart>
  );
};

/** Stap 1b: wachten op e-mailverificatie. De pagina pollt; na verificatie springt de wizard vanzelf door. */
export const StapVerificatie: React.FC<{ email: string }> = ({ email }) => {
  const { t } = useTranslation('pages');
  const opnieuw = useMutation({ mutationFn: verstuurVerificatieOpnieuw });

  return (
    <Kaart className="text-center">
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-sky-400/10 ring-1 ring-sky-400/30">
        <MailCheck className="h-8 w-8 text-sky-400" aria-hidden="true" />
      </div>
      <h2 className="text-2xl font-bold text-white">{t('opleiderWizard.verificatie.titel', { defaultValue: 'Bevestig uw e-mailadres' })}</h2>
      <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">
        {t('opleiderWizard.verificatie.intro', {
          defaultValue: 'We hebben een verificatielink gestuurd naar {{email}}. Klik op de link; deze pagina springt daarna automatisch door naar Stap 2.',
          email,
        })}
      </p>
      <p className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500" role="status">
        <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        {t('opleiderWizard.verificatie.wachten', { defaultValue: 'Wachten op verificatie…' })}
      </p>
      <div className="mt-6 space-y-3">
        {opnieuw.isSuccess && <Melding soort="ok">{t('opleiderWizard.verificatie.verstuurd', { defaultValue: 'Nieuwe verificatiemail verstuurd.' })}</Melding>}
        {opnieuw.isError && <Melding>{foutMelding(opnieuw.error, t('opleiderWizard.verificatie.fout', { defaultValue: 'Versturen mislukt.' }))}</Melding>}
        <Knop variant="secundair" bezig={opnieuw.isPending} onClick={() => opnieuw.mutate()}>
          {t('opleiderWizard.verificatie.opnieuw', { defaultValue: 'Mail opnieuw versturen' })}
        </Knop>
      </div>
    </Kaart>
  );
};
